/**
 * 브라우저 자동 번역(Chrome · Edge 의 Google/Microsoft 번역)이 DOM 을 바꿔 React 가 죽는 것을 막는다.
 *
 * 번역기는 텍스트 노드를 <font> 로 감싸거나 옮긴다. React 는 자기가 그린 노드를 기억하고 있다가 커밋 때
 * parent.removeChild(child) · parent.insertBefore(node, ref) 를 부르는데, 그 노드가 이미 다른 부모 아래로 옮겨져
 *   NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.
 *   NotFoundError: Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.
 * 로 화면 전체가 장애 페이지(_error)로 떨어진다. 해외 법인(미국 · 홍콩 · 중국 · 베트남) 사용자 신고의 대부분이 이것이었다
 * (alert-portal-app-prd, 2026-06 ~ 10). 스택에 앱 코드 없이 React 프레임워크만 있는 것이 특징.
 *
 * 번역을 막지는 않는다(notranslate 는 해외 사용자가 번역에 기대는 화면을 깨뜨린다). 대신 부모가 아닌 노드를
 * 빼거나 그 앞에 넣으라고 하면 예외 대신 조용히 넘긴다 — facebook/react#11538 에서 권장된 방식.
 * 실제로 걸린 적이 있으면 한 번만 console.warn 으로 남긴다.
 */
let installed = false;
let warned = false;

const warnOnce = (method: string) => {
  if (warned) return;
  warned = true;
  // eslint-disable-next-line no-console
  console.warn(
    `[domTranslateGuard] ${method}: React 가 그린 노드가 다른 부모로 옮겨져 있어 건너뜀 — 브라우저 자동 번역이 켜져 있을 가능성`,
  );
};

export const installDomTranslateGuard = () => {
  if (installed) return;
  if (typeof window === 'undefined' || typeof Node === 'undefined') return;
  installed = true;

  const { removeChild, insertBefore } = Node.prototype;

  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      warnOnce('removeChild');
      return child;
    }
    return removeChild.call(this, child) as T;
  };

  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      warnOnce('insertBefore');
      return newNode;
    }
    return insertBefore.call(this, newNode, referenceNode) as T;
  };
};

/** 테스트용 — 원래 메서드로 되돌린다 */
export const __resetDomTranslateGuardForTest = (
  original: Pick<Node, 'removeChild' | 'insertBefore'>,
) => {
  Node.prototype.removeChild = original.removeChild;
  Node.prototype.insertBefore = original.insertBefore;
  installed = false;
  warned = false;
};
